import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't redirect to login if we're on the admin page or login page
    const isAdminPage = window.location.pathname.startsWith('/admin');
    const isLoginPage = window.location.pathname === '/login';
    const isRegisterPage = window.location.pathname === '/register';
    const isAuthPage = isLoginPage || isRegisterPage;
    
    if (error.response?.status === 401 && !isAdminPage && !isAuthPage) {
      // Clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/signup', data),
  verifyRegistration: (email, code) => api.post('/auth/verify-registration', { email, code }),
  resendVerificationCode: (email) => api.post('/auth/resend-verification-code', { email }),
  logout: () => api.post('/auth/logout'),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  verifyResetCode: (email, code) => api.post('/auth/verify-reset-code', { email, code, purpose: 'password_reset' }),
  completePasswordReset: (email, code, new_password) => api.post('/auth/complete-password-reset', { email, code, new_password }),
  changePassword: (old_password, new_password) => api.post('/auth/change-password', { old_password, new_password }),
  requestEmailChange: (new_email) => api.post('/auth/request-email-change', { new_email }),
  confirmEmailChange: (code, new_email) => api.post('/auth/confirm-email-change', { code, new_email }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken })
};

// User API
export const userApi = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/me', data),
  getDepartments: () => api.get('/users/departments')
};

// Wallet API
export const walletApi = {
  getWallet: () => api.get('/wallet/'),
  initiateDeposit: (amount) => api.post('/wallet/deposit', { amount }),
  verifyPayment: (transactionId) => {
    return api.get('/wallet/verify-payment', { params: { transactionId } });
  },
  getTransactions: (limit = 50, offset = 0) => 
    api.get(`/wallet/transactions?limit=${limit}&offset=${offset}`)
};

// Subscription Types API
export const subscriptionTypesApi = {
  getAll: (includeInactive = false) => 
    api.get(`/subscription-types/?include_inactive=${includeInactive}`),
  getById: (id) => api.get(`/subscription-types/${id}`),
  create: (data) => api.post('/subscription-types/', data),
  update: (id, data) => api.put(`/subscription-types/${id}`, data),
  delete: (id) => api.delete(`/subscription-types/${id}`)
};

// Subscriptions API
export const subscriptionsApi = {
  getMySubscriptions: () => api.get('/subscriptions/'),
  subscribe: (subscriptionTypeId) => 
    api.post('/subscriptions/', { subscription_type_id: subscriptionTypeId }),
  cancel: (subscriptionId) => api.post(`/subscriptions/${subscriptionId}/cancel`),
  getCodes: (subscriptionId) => api.get(`/subscriptions/${subscriptionId}/codes`)
};

// Accounts API (Admin)
export const accountsApi = {
  getAll: (subscriptionTypeId = null) => {
    const params = subscriptionTypeId ? `?subscription_type_id=${subscriptionTypeId}` : '';
    return api.get(`/accounts/${params}`);
  },
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts/', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
  getUsers: (accountId) => api.get(`/accounts/${accountId}/users`),
  moveUser: (data) => api.post('/accounts/move-user', data),
  sendCode: (subscriptionTypeId, accountEmail, code) => 
    api.post(`/accounts/send-code?subscription_type_id=${subscriptionTypeId}&account_email=${accountEmail}&code=${code}`)
};

// Admin API
export const adminApi = {
  login: (email, password, totpCode = null) => 
    api.post('/admin/login', { email, password, totp_code: totpCode }),
  getDashboardStats: () => api.get('/admin/dashboard-stats'),
  getUsers: (search = '', limit = 50, offset = 0) => 
    api.get(`/admin/users?search=${search}&limit=${limit}&offset=${offset}`),
  enrollMfa: () => api.post('/admin/mfa/enroll'),
  verifyMfa: (code) => api.post('/admin/mfa/verify', { code }),
  disableMfa: (code) => api.post('/admin/mfa/disable', { code }),
  getMfaStatus: () => api.get('/admin/mfa/status')
};

export default api;
